import React from "react";
import TransactionStatement from "../../components/TransactionStatement";

export default function PensionTransactionStatement({ transactionHistory = {}, companyName = '', companyId, signatureUrl = '/signature.png' }) {
  const transactions = transactionHistory?.transaction_history?.transactions || [];

  // Normalize incoming transaction items into rows
  const rows = Array.isArray(transactions) ? transactions.map(item => {
    const date = item.payment_date || '';
    const type = item.pension_type_name || '';
    const scheme_name = item.scheme_name || '';
    const amountRaw = item.received || 0;
    const amount = Number(String(amountRaw).replace(/,/g, '')) || 0;
    const units_allocated = item.units_allocated || '';
    const unit_price = item.unit_price || '';
    const narration = item.narration || '';
    const employer_contribution = item.employer_contribution || '';
    return { date, type, scheme_name, amount, units_allocated, unit_price, narration, employer_contribution };
  }) : [];

  const fmt = (v) => `GHS ${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  // Calculate dynamic column widths based on content
  // Priority: SCHEME NAME > PENSION TYPE > AMOUNT > others
  const getColumnWidths = () => {
    // Give SCHEME NAME more width to fit on 2 lines max
    // Reduce other columns to compensate
    const colAnalysis = [
      { title: 'DATE', key: 'date', width: 9 },
      { title: 'PENSION TYPE', key: 'type', width: 12 },
      { title: 'SCHEME NAME', key: 'scheme_name', width: 38 },
      { title: 'AMOUNT', key: 'amount', width: 11 },
      { title: 'UNITS', key: 'units_allocated', width: 7 },
      { title: 'UNIT PRICE', key: 'unit_price', width: 8 },
      { title: 'EMPLOYER CONTRIB.', key: 'employer_contribution', width: 10 },
      { title: 'NARRATION', key: 'narration', width: 5 }
    ];
    
    const widths = colAnalysis.map(col => col.width);
    
    const total = widths.reduce((a, b) => a + b, 0);
    return widths.map(w => (w / total) * 100);
  };
  
  const dynamicWidths = getColumnWidths();

  const columns = [
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
      widthStyle: { width: `${dynamicWidths[0]}%`, borderRight: '1px solid #888', textAlign: 'center', whiteSpace: 'nowrap' },
      render: (date) => date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    },
    {
      title: "PENSION TYPE",
      dataIndex: "type",
      key: "type",
      widthStyle: { 
        width: `${dynamicWidths[1]}%`, 
        borderRight: '1px solid #888', 
        textAlign: 'center', 
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    },
    {
      title: "SCHEME NAME",
      dataIndex: "scheme_name",
      key: "scheme_name",
      widthStyle: { 
        width: `${dynamicWidths[2]}%`, 
        borderRight: '1px solid #888', 
        textAlign: 'center',
        whiteSpace: 'normal'
      }
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      key: "amount",
      widthStyle: { width: `${dynamicWidths[3]}%`, borderRight: '1px solid #888', textAlign: 'center', whiteSpace: 'nowrap' },
      render: (amount) => fmt(amount)
    },
    {
      title: "UNITS",
      dataIndex: "units_allocated",
      key: "units_allocated",
      widthStyle: { width: `${dynamicWidths[4]}%`, borderRight: '1px solid #888', textAlign: 'center' }
    },
    {
      title: "UNIT PRICE",
      dataIndex: "unit_price",
      key: "unit_price",
      widthStyle: { width: `${dynamicWidths[5]}%`, borderRight: '1px solid #888', textAlign: 'center' },
      render: (v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    {
      title: "EMPLOYER CONTRIB.",
      dataIndex: "employer_contribution",
      key: "employer_contribution",
      widthStyle: { width: `${dynamicWidths[6]}%`, borderRight: '1px solid #888', textAlign: 'center' },
      render: (v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    {
      title: "NARRATION",
      dataIndex: "narration",
      key: "narration",
      widthStyle: { width: `${dynamicWidths[7]}%`, borderRight: 'none', textAlign: 'center' }
    }
  ];

  return (
    <div className="pension-statement-table">
      <TransactionStatement
        companyName={companyName}
        rows={rows}
        columns={columns}
        companyId={companyId}
        signatureUrl={signatureUrl}
        firstPageSize={11}
        pageSize={18}
      />
    </div>
  );
}
